/**
 * KorvoError: API-reachable errors with base code + unique throw-site id.
 * Constructor: new KorvoError(baseCode, uniqueThrowId, options?)
 * - baseCode: e.g. ERR_EDITOR_THREADS_LOAD, ERR_AUTH_401
 * - uniqueThrowId: 1–999, unique per throw site (each place that throws uses a different number)
 * User-visible six-digit code = catalogCode (from ERROR_REGISTRY for base) × 1000 + uniqueThrowId (e.g. 114001).
 * See docs/ERROR_CODES.md.
 */

const COMPOSITE_SEP = ':';

export class KorvoError extends Error {
  /** Composite id "BASE:uniqueThrowId" for API response lookup (e.g. ERR_EDITOR_THREADS_LOAD:1). */
  readonly id: string;
  readonly baseCode: string;
  readonly uniqueThrowId: number;
  readonly status?: number;
  readonly details?: unknown;

  constructor(
    baseCode: string,
    uniqueThrowId: number,
    options?: { message?: string; status?: number; details?: unknown }
  ) {
    const message = options?.message ?? `Error: ${baseCode}`;
    super(message);
    this.name = 'KorvoError';
    this.baseCode = baseCode;
    this.uniqueThrowId = uniqueThrowId >= 1 && uniqueThrowId <= 999 ? uniqueThrowId : 1;
    this.id = `${baseCode}${COMPOSITE_SEP}${this.uniqueThrowId}`;
    this.status = options?.status;
    this.details = options?.details;
    if (typeof Error.captureStackTrace === 'function') {
      Error.captureStackTrace(this, KorvoError);
    }
  }
}

export const KORVO_ERROR_COMPOSITE_SEP = COMPOSITE_SEP;

/**
 * True when `error` is a {@link KorvoError} from this module, or a **structurally equivalent**
 * object (same `name`, `baseCode`, `uniqueThrowId`) thrown across a different bundled copy of
 * `@korvo/core` in the same process — `instanceof` alone can be false in that case.
 */
export function isKorvoError(error: unknown): error is KorvoError {
  if (error instanceof KorvoError) {
    return true;
  }
  if (typeof error !== 'object' || error === null) {
    return false;
  }
  const o = error as Record<string, unknown>;
  return (
    o.name === 'KorvoError' &&
    typeof o.baseCode === 'string' &&
    typeof o.uniqueThrowId === 'number'
  );
}
